import { changeSidToName, changeWFTypeToApproveType, formatDMY, formatDocDate, numberWithCommas } from "modules/Common";

// Maps workflowTypeConfig keys → actual data fields
export const DISPLAY_TO_FIELD_MAP: Record<string, string> = {
    display_docno: "docNo",
    display_docdate: "docDate",
    display_fiscalyear: "docYear",
    display_created_by: "requestorName",
    display_created_on: "createdOnFormatDateTime",
    display_updated_by: "updated_byname",
    display_updated_on: "updatedOnFormatDateTime",
    display_amount: "docAmount",
    display_currency: "docCurrency",
    display_duedate: "invoiceDueDate",
    display_paymentdate: "invoicePaymentDate",
    display_bp: "bpName",
    display_remark: "requestorMessage",
};

export const buildIncludeKeys = (workflowTypeConfig?: any): string[] => {
    if (!workflowTypeConfig) return [];

    return Object.entries(workflowTypeConfig)
        .filter(([key, value]) => key.startsWith("display_") && value === "Y")
        .map(([key]) => DISPLAY_TO_FIELD_MAP[key])
        .filter(Boolean); // drop undefined mappings
};

export const buildSearchableRow = (row: any, props: any) => {
    const {
        sidArr, // for changeSidToName
    } = props;

    const formattedStrings: string[] = [];

    // Dates
    if (row.docDate) {
        formattedStrings.push(formatDocDate(String(row.docDate)));
    }

    if (row.invoiceDueDate) {
        formattedStrings.push(formatDocDate(String(row.invoiceDueDate)));
    }

    if (row.invoicePaymentDate) {
        formattedStrings.push(formatDocDate(String(row.invoicePaymentDate)));
    }

    // Created / Updated time formatting
    if (row.createdOnFormatDateTime) {
        formattedStrings.push(formatDMY(String(row.createdOnFormatDateTime)));
    }

    if (row.updatedOnFormatDateTime) {
        formattedStrings.push(formatDMY(String(row.updatedOnFormatDateTime)));
    }

    // Amount formatting
    if (row.docAmount) {
        formattedStrings.push(numberWithCommas(String(row.docAmount)));
    }

    // SID → Name mapping
    if (row.app_id) {
        formattedStrings.push(changeSidToName(row.app_id, sidArr));
    }

    // WF Type formatting
    if (row.wfType) {
        formattedStrings.push(changeWFTypeToApproveType(row.wfType));
    }

    // Remove empty values & lowercase
    const searchable = formattedStrings
        .filter(Boolean)
        .map(s => String(s).toLowerCase());

    return {
        ...row,
        _searchValues: searchable,
    };
};


export const matchesKeyword = (
    obj: Record<string, any>,
    normalizedKeyword: string,
    options: { includeKeys?: string[]; excludeKeys?: string[] } = {}
): boolean => {
    const { includeKeys = [], excludeKeys = [] } = options;

    // Check RAW fields
    for (const [key, rawValue] of Object.entries(obj)) {
        if (excludeKeys.includes(key)) continue;
        if (includeKeys.length > 0 && !includeKeys.includes(key)) continue;

        if (String(rawValue ?? "").toLowerCase().includes(normalizedKeyword)) {
            return true;
        }
    }

    // Check formatted / virtual fields
    if (Array.isArray(obj._searchValues)) {
        if (obj._searchValues.some(v => v.includes(normalizedKeyword))) {
            return true;
        }
    }

    return false;
};

export const performSearch = (
    batches: any[],
    keyword: string,
    options: { includeKeys?: string[]; excludeKeys?: string[] } = {},
    props: any
): any[] => {
    const normalized = keyword.trim().toLowerCase();
    if (!normalized) return [];

    return batches
        .map(batch => {
            const searchableBatch = buildSearchableRow(batch, props);

            const filteredResults = (batch.result_list || [])
                .map(row => buildSearchableRow(row, props))
                .filter(row => matchesKeyword(row, normalized, options));

            const batchMatches = matchesKeyword(searchableBatch, normalized, options);

            if (batchMatches || filteredResults.length > 0) {
                return { ...batch, result_list: filteredResults };
            }

            return null;
        })
        .filter(Boolean);
};



// export const matchesKeyword = (
//     obj: Record<string, any>,
//     normalizedKeyword: string,
//     options: { includeKeys?: string[]; excludeKeys?: string[] } = {}
// ): boolean => {
//     const { includeKeys = [], excludeKeys = [] } = options;

//     for (const [key, rawValue] of Object.entries(obj)) {
//         // Exclude has highest priority — skip immediately if in exclude list
//         if (excludeKeys.includes(key)) continue;

//         // If include list exists and the key is not in it — skip
//         if (includeKeys.length > 0 && !includeKeys.includes(key)) continue;

//         const value = rawValue ?? "";

//         // Compare lowercase value
//         if (String(value).toLowerCase().includes(normalizedKeyword)) {
//             return true;
//         }
//     }

//     return false;
// };


// export const performSearch = (
//     batches: any[],
//     keyword: string,
//     options: { includeKeys?: string[]; excludeKeys?: string[] } = {}
// ): any[] => {
//     const normalized = keyword.trim().toLowerCase();
//     if (!normalized) {
// 		return [];
// 	}

//     const filteredData =  (Array.isArray(batches) ? batches : [])
//         .map(batch => {
//             const batchMatches = matchesKeyword(
//                 batch,
//                 normalized,
//                 options
//             );

//             const filteredResults = (batch.result_list || []).filter(row =>
//                 matchesKeyword(row, normalized, options)
//             );

//             // Include batch if it or any result matches
//             if (batchMatches || filteredResults.length > 0) {
//                 return { ...batch, result_list: filteredResults };
//             }

//             return null;
//         })
//         .filter(Boolean);

// 		return filteredData
// };

// // Utility: check if object has any value that matches keyword
// export const matchesKeyword = (obj, keyword, options = {}) => {
//     // @ts-ignore
//     const { includeKeys = [], excludeKeys = [] } = options;

//     return Object.entries(obj).some(([key, value]) => {
//         // Exclude has highest priority — skip immediately if in exclude list
//         if (excludeKeys.includes(key)) return false;

//         // If include list exists and the key is not in it — skip
//         if (includeKeys.length > 0 && !includeKeys.includes(key)) return false;

//         // Compare lowercase value
//         return String(value ?? '')
//             .toLowerCase()
//             .includes(keyword);
//     });
// };

// export const performSearch = (valueBatchAll, keyword, options = {}) => {
//     // @ts-ignore
//     const { includeKeys = [], excludeKeys = [] } = options;

//     const searchOptions = { includeKeys, excludeKeys };

//     const normalizedKeyword = keyword.trim().toLowerCase();
//     if (!normalizedKeyword) {
//         return [];
//     }

//     // Filter batches and nested result_list
//     const filteredSearchData = (
//         Array.isArray(valueBatchAll) ? valueBatchAll : []
//     )
//         .map(batch => {
//             const batchMatches = matchesKeyword(
//                 batch,
//                 normalizedKeyword,
//                 searchOptions
//             );

//             const filteredResults = (batch.result_list || []).filter(row =>
//                 matchesKeyword(row, normalizedKeyword, searchOptions)
//             );

//             // Include batch if it or any result matches
//             if (batchMatches || filteredResults.length > 0) {
//                 return { ...batch, result_list: filteredResults };
//             }

//             return null;
//         })
//         .filter(Boolean); // Remove nulls

//     return filteredSearchData;
// };
